import { useParams } from 'react-router-dom';
import { Check, X, ArrowUpRight, TrendingUp, DollarSign, Zap, Shield, Users } from 'lucide-react';
import SEO from '@/components/SEO';
import { site } from '@/data/content';

function renderValue(value: boolean | string) {
  if (value === true) return <Check className="h-5 w-5 text-green-600" aria-label="Included" />;
  if (value === false) return <X className="h-5 w-5 text-slate-300" aria-label="Not included" />;
  return <span className="text-sm font-semibold text-brand-700">{value}</span>;
}

function score(value: boolean | string) {
  if (value === true) return 1;
  if (value === false) return 0;
  return 0.5;
}

const comparisonMap: Record<string, {
  title: string;
  description: string;
  left: string;
  right: string;
  eyebrow: string;
  headline: string;
  subhead: string;
  intro: string;
  pain: string[];
  rows: { feature: string; zurihub: boolean | string; them: boolean | string }[];
  highlights: { title: string; text: string }[];
  migration: { step: number; title: string; text: string }[];
  faqs: { q: string; a: string }[];
}> = {
  'zendesk-vs-zurihub': {
    title: 'Zendesk vs ZuriHub',
    description: 'Compare Zendesk and ZuriHub on AI, channels, voice, pricing, and analytics.',
    left: 'Zendesk',
    right: 'ZuriHub',
    eyebrow: 'Head-to-head comparison',
    headline: 'Zendesk is mature. ZuriHub is modern.',
    subhead: 'Why teams are switching from Zendesk to ZuriHub for AI, channels, and value.',
    intro: 'Zendesk is a mature ticketing suite with a large feature list, but its per-agent cost, add-on pricing, and fragmented channels can scale quickly. ZuriHub bundles chat, tickets, AI, analytics, and voice in one workspace.',
    pain: [
      'Add-on pricing hides the true cost of AI and channels.',
      'Modern messaging apps are treated as secondary channels.',
      'Analytics, voice, and coaching live in separate products.',
    ],
    rows: [
      { feature: 'Unified inbox for all channels', zurihub: true, them: true },
      { feature: 'AI reply suggestions included', zurihub: true, them: 'Add-on' },
      { feature: 'WhatsApp, Instagram, Telegram in one place', zurihub: true, them: 'Limited / extra cost' },
      { feature: 'No annual lock-in', zurihub: true, them: false },
      { feature: 'Voice calls built in', zurihub: 'Pro & Enterprise', them: 'Enterprise add-on' },
      { feature: 'Heatmaps and session replay', zurihub: true, them: false },
      { feature: 'Transparent per-agent price', zurihub: true, them: false },
      { feature: 'AI agent coaching', zurihub: true, them: 'Advanced add-on' },
    ],
    highlights: [
      { title: 'One workspace', text: 'Chat, tickets, voice, and analytics in one platform instead of separate modules.' },
      { title: 'Predictable pricing', text: 'Pro is $25/agent and includes core AI and all channels. No hidden feature tiers.' },
      { title: 'Faster setup', text: 'Connect messaging channels and launch the widget in minutes, not weeks.' },
    ],
    migration: [
      { step: 1, title: 'Export your data', text: 'Download tickets, contacts, and macros from Zendesk.' },
      { step: 2, title: 'Import to ZuriHub', text: 'Use our CSV import or migration service to load your data.' },
      { step: 3, title: 'Connect channels', text: 'Add WhatsApp, Instagram, email, and chat in one afternoon.' },
      { step: 4, title: 'Train and go live', text: 'Point Zyra AI at your docs and start supporting customers.' },
    ],
    faqs: [
      { q: 'Is ZuriHub cheaper than Zendesk?', a: 'For most teams, yes. ZuriHub Pro includes AI and all channels at $25/agent, while Zendesk often requires add-ons for the same capabilities.' },
      { q: 'Can I migrate from Zendesk?', a: 'Yes. You can export tickets and contacts and import them into ZuriHub.' },
      { q: 'Does ZuriHub support enterprise needs?', a: 'Yes. SSO, custom SLAs, private cloud, and 99.99% uptime are available on Enterprise.' },
    ],
  },
  'zendesk-vs-freshdesk': {
    title: 'Zendesk vs Freshdesk',
    description: 'Side-by-side comparison of two legacy help desks and why teams look at ZuriHub instead.',
    left: 'Zendesk',
    right: 'Freshdesk',
    eyebrow: 'Versus comparison',
    headline: 'Zendesk vs Freshdesk: the real winner is neither',
    subhead: 'See how ZuriHub beats both on channels, AI, and total cost of ownership.',
    intro: 'Zendesk and Freshdesk are the two most known help-desk tools. Both are solid for email-to-ticket, but both push advanced features, AI, and omnichannel into add-ons. ZuriHub replaces the whole stack at one flat price.',
    pain: [
      'AI, chatbots, and WhatsApp cost extra on both platforms.',
      'Neither gives you live visitor and website behavior data.',
      'Legacy pricing makes it hard to predict monthly spend.',
    ],
    rows: [
      { feature: 'Core ticket management', zurihub: 'ZuriHub included', them: true },
      { feature: 'Live chat and chatbots', zurihub: 'ZuriHub included', them: 'Add-on' },
      { feature: 'AI reply suggestions', zurihub: 'ZuriHub included', them: 'Add-on' },
      { feature: 'WhatsApp Business API', zurihub: 'ZuriHub ready', them: 'Third-party connector' },
      { feature: 'Website heatmaps', zurihub: 'ZuriHub add-on', them: false },
      { feature: 'Voice calls in inbox', zurihub: 'ZuriHub Enterprise', them: 'Add-on' },
      { feature: 'One transparent price', zurihub: 'ZuriHub Pro $25/agent', them: false },
    ],
    highlights: [
      { title: 'Modern channels first', text: 'ZuriHub treats WhatsApp, Instagram, and Telegram as first-class channels, not afterthoughts.' },
      { title: 'Built-in AI', text: 'Zyra AI is included in Pro, not an expensive add-on.' },
      { title: 'Website context', text: 'Heatmaps and live visitor data give agents the full customer picture.' },
    ],
    migration: [
      { step: 1, title: 'Pick your current tool', text: 'Export tickets and contacts from either Zendesk or Freshdesk.' },
      { step: 2, title: 'Map your data', text: 'Match fields and statuses to ZuriHub ticket types.' },
      { step: 3, title: 'Connect channels', text: 'Move live chat, email, and messaging into ZuriHub.' },
      { step: 4, title: 'Launch with AI', text: 'Activate Zyra AI and go live with your team.' },
    ],
    faqs: [
      { q: 'Which is better for small teams?', a: 'Freshdesk is often cheaper for basic ticketing, but ZuriHub gives you more channels and AI at a similar price.' },
      { q: 'Does either have live visitor tracking?', a: 'Neither includes website analytics by default. ZuriHub does.' },
      { q: 'Can I switch from Zendesk or Freshdesk?', a: 'Yes. Both support exports that can be imported into ZuriHub.' },
    ],
  },
  'intercom-vs-zurihub': {
    title: 'Intercom vs ZuriHub',
    description: 'Compare Intercom and ZuriHub for support, sales, and AI messaging.',
    left: 'Intercom',
    right: 'ZuriHub',
    eyebrow: 'Support vs marketing',
    headline: 'Intercom is for bots. ZuriHub is for outcomes.',
    subhead: 'Get the full support stack — tickets, SLAs, knowledge base, and voice — without message-based pricing.',
    intro: 'Intercom excels at conversational marketing and chatbots. ZuriHub adds the full support stack — tickets, SLAs, knowledge base, and voice — with simpler pricing and more channels.',
    pain: [
      'Message-based pricing gets expensive fast.',
      'Support, SLA, and voice are paid add-ons.',
      'WhatsApp and Instagram support is limited.',
    ],
    rows: [
      { feature: 'Live chat and bots', zurihub: true, them: true },
      { feature: 'Ticketing and SLA management', zurihub: true, them: 'Add-on' },
      { feature: 'Knowledge base with custom domain', zurihub: true, them: 'Add-on' },
      { feature: 'AI agent coaching', zurihub: true, them: 'Advanced add-on' },
      { feature: 'WhatsApp, Instagram, Telegram', zurihub: true, them: 'Limited' },
      { feature: 'Transparent per-agent price', zurihub: true, them: false },
      { feature: 'Voice calls in the inbox', zurihub: 'Enterprise', them: false },
      { feature: 'Website heatmaps', zurihub: true, them: false },
    ],
    highlights: [
      { title: 'Support, not just chat', text: 'ZuriHub includes ticketing, SLAs, and knowledge base as core features.' },
      { title: 'More messaging channels', text: 'WhatsApp, Telegram, Instagram, and email alongside live chat.' },
      { title: 'No message-based pricing', text: 'ZuriHub is per agent, not per active contact or message.' },
    ],
    migration: [
      { step: 1, title: 'Export conversations', text: 'Download contacts and conversation history from Intercom.' },
      { step: 2, title: 'Import to ZuriHub', text: 'Load contacts and historical chats in minutes.' },
      { step: 3, title: 'Add ticketing and SLAs', text: 'Set response targets and start managing real support cases.' },
      { step: 4, title: 'Enable voice and AI', text: 'Add voice on Enterprise and let Zyra coach your team.' },
    ],
    faqs: [
      { q: 'Is Intercom better for marketing?', a: 'Intercom has strong messaging automation for marketing. ZuriHub is built for support, sales, and operations.' },
      { q: 'Can I use ZuriHub for inbound sales?', a: 'Yes. Live chat, routing, and lead capture work well for sales teams.' },
      { q: 'How is pricing different?', a: 'ZuriHub uses flat per-agent pricing. Intercom often charges based on contacts and add-ons.' },
    ],
  },
  'freshdesk-vs-zurihub': {
    title: 'Freshdesk vs ZuriHub',
    description: 'See how Freshdesk compares to ZuriHub on channels, AI, analytics, and voice.',
    left: 'Freshdesk',
    right: 'ZuriHub',
    eyebrow: 'Growth comparison',
    headline: 'Outgrow Freshdesk without outgrowing your budget',
    subhead: 'ZuriHub gives you the channels, AI, and analytics Freshdesk customers usually pay extra for.',
    intro: 'Freshdesk is popular for email-to-ticket and a free tier. ZuriHub replaces the whole support stack and adds modern channels, AI, and website analytics in one workspace.',
    pain: [
      'Omnichannel and AI require premium tiers.',
      'No built-in website visitor analytics.',
      'Voice and proactive tools are limited or extra.',
    ],
    rows: [
      { feature: 'Email, chat, social support', zurihub: true, them: true },
      { feature: 'WhatsApp Business API ready', zurihub: true, them: 'Third-party connector' },
      { feature: 'AI reply suggestions from your docs', zurihub: true, them: 'Add-on' },
      { feature: 'Website analytics and heatmaps', zurihub: true, them: false },
      { feature: 'Voice calls and transcription', zurihub: 'Enterprise', them: 'Add-on' },
      { feature: 'Proactive support tools', zurihub: true, them: 'Limited' },
      { feature: 'One flat Pro price', zurihub: '$25/agent', them: 'Tiered' },
    ],
    highlights: [
      { title: 'Beyond tickets', text: 'ZuriHub gives you the full customer journey, from website visit to resolution.' },
      { title: 'AI built in', text: 'Zyra AI drafts replies and coaches agents in real time at no extra cost on Pro.' },
      { title: 'Voice included', text: 'Enterprise plans include AI voice calls and transcription in the inbox.' },
    ],
    migration: [
      { step: 1, title: 'Export from Freshdesk', text: 'Download tickets, contacts, and knowledge base articles.' },
      { step: 2, title: 'Import to ZuriHub', text: 'Upload your data and map ticket fields.' },
      { step: 3, title: 'Connect messaging', text: 'Add WhatsApp, Instagram, email, and live chat.' },
      { step: 4, title: 'Publish your portal', text: 'Launch the knowledge base and start supporting customers.' },
    ],
    faqs: [
      { q: 'Is Freshdesk cheaper than ZuriHub?', a: 'Freshdesk has a free tier for one agent. ZuriHub Pro is $25 and includes more channels and AI.' },
      { q: 'Can I keep my Freshdesk data?', a: 'Yes. Export tickets and contacts and import them into ZuriHub.' },
      { q: 'Does ZuriHub have a free plan?', a: 'Yes. One agent is free with core chat and ticket features.' },
    ],
  },
  'tidio-vs-zurihub': {
    title: 'Tidio vs ZuriHub',
    description: 'Compare Tidio and ZuriHub on live chat, AI, ticketing, channels, and pricing for support and sales teams.',
    left: 'Tidio',
    right: 'ZuriHub',
    eyebrow: 'Head-to-head comparison',
    headline: 'Tidio is chat-first. ZuriHub is the full support stack.',
    subhead: 'Why teams switch from Tidio to ZuriHub for tickets, channels, and AI.',
    intro: 'Tidio is a popular live chat and chatbot tool for small sites. ZuriHub replaces the whole support stack — tickets, SLAs, knowledge base, WhatsApp, Instagram, Telegram, voice, and analytics — in one workspace.',
    pain: [
      'Ticketing, knowledge base, and SLAs require plan upgrades or are missing.',
      'AI and automation are priced per conversation and can scale fast.',
      'WhatsApp, Instagram, and Telegram feel like add-ons rather than core channels.',
    ],
    rows: [
      { feature: 'Live chat and chatbot', zurihub: true, them: true },
      { feature: 'Ticketing and SLA management', zurihub: true, them: 'Limited' },
      { feature: 'Knowledge base with custom domain', zurihub: true, them: 'Add-on' },
      { feature: 'AI reply suggestions included', zurihub: true, them: 'Usage-based add-on' },
      { feature: 'WhatsApp, Instagram, Telegram', zurihub: true, them: 'Limited' },
      { feature: 'Transparent per-agent price', zurihub: '$25/agent', them: 'Usage-based' },
      { feature: 'Voice calls in the inbox', zurihub: 'Enterprise', them: false },
      { feature: 'Website heatmaps and session replay', zurihub: true, them: false },
      { feature: 'No annual lock-in', zurihub: true, them: false },
    ],
    highlights: [
      { title: 'Full support stack', text: 'Tickets, SLAs, knowledge base, and analytics are included, not bolted on.' },
      { title: 'Predictable per-agent price', text: 'ZuriHub Pro is $25 per agent with no usage-based AI surprise bills.' },
      { title: 'Modern channels', text: 'WhatsApp, Instagram, Telegram, and email are first-class channels in one inbox.' },
    ],
    migration: [
      { step: 1, title: 'Export Tidio chats', text: 'Download contacts and conversation history from Tidio.' },
      { step: 2, title: 'Import to ZuriHub', text: 'Load customers and past conversations in minutes.' },
      { step: 3, title: 'Add tickets and SLAs', text: 'Set up real support cases and response targets.' },
      { step: 4, title: 'Connect channels', text: 'Add live chat, WhatsApp, Instagram, and email.' },
    ],
    faqs: [
      { q: 'Is ZuriHub better than Tidio for support?', a: 'For teams that need tickets, SLAs, and a knowledge base, yes. Tidio is stronger for simple chat and bots on small sites.' },
      { q: 'How is pricing different?', a: 'Tidio uses plan + usage pricing. ZuriHub is a flat $25 per agent on Pro with AI and all channels included.' },
      { q: 'Can I move my Tidio chat history?', a: 'Yes. Contacts and conversation exports can be imported into ZuriHub.' },
    ],
  },
  'kustomer-vs-zurihub': {
    title: 'Kustomer vs ZuriHub',
    description: 'Compare Kustomer and ZuriHub on CRM, AI, channels, pricing, and ease of setup for customer service teams.',
    left: 'Kustomer',
    right: 'ZuriHub',
    eyebrow: 'Head-to-head comparison',
    headline: 'Kustomer is a CRM. ZuriHub is a complete CX workspace.',
    subhead: 'Why teams are switching from Kustomer to ZuriHub for price, setup speed, and AI value.',
    intro: 'Kustomer is a customer service CRM built for large omnichannel teams, with an eight-seat minimum, annual contracts, and AI sold as add-ons. ZuriHub delivers the same channels, AI, and analytics at a flat $25 per agent with no seat minimum and no annual lock-in.',
    pain: [
      'Plans start at $89 per seat with an eight-seat annual minimum.',
      'AI add-ons cost $0.60 per engaged conversation or $40 per agent per month.',
      'Complex setup and pricing make it hard for small and mid-size teams.',
    ],
    rows: [
      { feature: 'Unified inbox for all channels', zurihub: true, them: true },
      { feature: 'AI reply suggestions included', zurihub: true, them: 'Add-on' },
      { feature: 'WhatsApp, Instagram, Telegram in one place', zurihub: true, them: true },
      { feature: 'Per-agent starting price', zurihub: '$25/agent', them: '$89/agent' },
      { feature: 'No annual contract', zurihub: true, them: false },
      { feature: 'No minimum seat count', zurihub: true, them: false },
      { feature: 'Heatmaps and session replay', zurihub: true, them: false },
      { feature: 'Voice calls in the inbox', zurihub: 'Enterprise', them: 'Add-on' },
      { feature: 'Free plan for 1 agent', zurihub: true, them: false },
    ],
    highlights: [
      { title: 'Transparent pricing', text: 'ZuriHub Pro is $25 per agent, with no eight-seat minimum or annual lock-in.' },
      { title: 'AI included', text: 'Zyra AI is built into Pro. No per-conversation or per-agent AI upcharges.' },
      { title: 'Faster setup', text: 'Connect channels and train AI in hours, not months.' },
    ],
    migration: [
      { step: 1, title: 'Export Kustomer data', text: 'Download tickets, contacts, and conversation history.' },
      { step: 2, title: 'Import to ZuriHub', text: 'Map customer fields and load conversations.' },
      { step: 3, title: 'Connect channels', text: 'Add WhatsApp, email, Instagram, live chat, and Telegram.' },
      { step: 4, title: 'Train and launch', text: 'Point Zyra AI at your docs and start supporting customers.' },
    ],
    faqs: [
      { q: 'Is Kustomer more expensive than ZuriHub?', a: 'Yes. Kustomer starts at $89 per seat with an eight-seat annual minimum, plus AI add-ons. ZuriHub Pro is $25 per agent with no minimum.' },
      { q: 'Can ZuriHub replace Kustomer?', a: 'For most teams, yes. ZuriHub covers the same channels and adds knowledge base, analytics, and AI without the enterprise overhead.' },
      { q: 'Does ZuriHub have a customer timeline like Kustomer?', a: 'Yes. Every contact has a unified conversation history across all connected channels.' },
    ],
  },
  'gorgias-vs-zurihub': {
    title: 'Gorgias vs ZuriHub',
    description: 'Compare Gorgias and ZuriHub for ecommerce support on Shopify, AI, channels, analytics, and pricing.',
    left: 'Gorgias',
    right: 'ZuriHub',
    eyebrow: 'Head-to-head comparison',
    headline: 'Gorgias is ecommerce-only. ZuriHub works for every business.',
    subhead: 'Why ecommerce and non-ecommerce teams choose ZuriHub over Gorgias for channels, AI, and value.',
    intro: 'Gorgias is built for Shopify and ecommerce with ticket-based pricing from $10 to $900 per month. ZuriHub gives ecommerce teams the same deep Shopify order data plus all channels, AI, and analytics in one workspace — without paying per ticket.',
    pain: [
      'Pricing is based on billable tickets, so busy months get expensive.',
      'Voice, SMS, and some channels are paid add-ons.',
      'It is designed for ecommerce, making it less flexible for services, SaaS, or education.',
    ],
    rows: [
      { feature: 'Ecommerce order data in tickets', zurihub: true, them: true },
      { feature: 'Live chat and ticketing', zurihub: true, them: true },
      { feature: 'AI reply suggestions included', zurihub: true, them: 'Add-on / usage' },
      { feature: 'WhatsApp, Instagram, Telegram, email', zurihub: true, them: true },
      { feature: 'Transparent per-agent price', zurihub: '$25/agent', them: 'Ticket-based' },
      { feature: 'Website heatmaps and session replay', zurihub: true, them: false },
      { feature: 'Voice calls in the inbox', zurihub: 'Enterprise', them: 'Add-on' },
      { feature: 'Works for non-ecommerce industries', zurihub: true, them: 'Limited' },
      { feature: 'Free plan for 1 agent', zurihub: true, them: false },
    ],
    highlights: [
      { title: 'No ticket-based billing', text: 'ZuriHub is $25 per agent, so spikes in volume do not spike your bill.' },
      { title: 'Ecommerce + everything else', text: 'Get Shopify order data plus support for services, SaaS, education, and more.' },
      { title: 'Built-in website analytics', text: 'Heatmaps, session replay, and live visitor data are all in the same workspace.' },
    ],
    migration: [
      { step: 1, title: 'Export Gorgias tickets', text: 'Download tickets, contacts, and macros.' },
      { step: 2, title: 'Import to ZuriHub', text: 'Map Shopify order fields and load conversations.' },
      { step: 3, title: 'Connect Shopify and channels', text: 'Add Shopify, WhatsApp, Instagram, email, and live chat.' },
      { step: 4, title: 'Launch with AI', text: 'Train Zyra on your policies and start deflecting FAQs.' },
    ],
    faqs: [
      { q: 'Is ZuriHub cheaper than Gorgias for ecommerce?', a: 'For most teams, yes. ZuriHub is $25 per agent and does not charge per ticket, so high-volume months stay predictable.' },
      { q: 'Does ZuriHub work with Shopify?', a: 'Yes. You can connect Shopify order data to conversations for the same context Gorgias provides.' },
      { q: 'Can non-ecommerce teams use ZuriHub?', a: 'Yes. ZuriHub supports services, SaaS, education, healthcare, finance, and logistics, not just ecommerce.' },
    ],
  },
};

export default function CompareDetail() {
  const { slug } = useParams<{ slug: string }>();
  const c = slug ? comparisonMap[slug] : undefined;

  if (!c) {
    return (
      <section className="px-4 pt-40 pb-24 text-center">
        <h1 className="text-3xl font-bold text-slate-900">Comparison not found</h1>
        <p className="mt-2 text-slate-600">Check the compare page for available comparisons.</p>
      </section>
    );
  }

  const zurihubScore = Math.round((c.rows.reduce((acc, r) => acc + score(r.zurihub), 0) / c.rows.length) * 100);
  const themScore = Math.round((c.rows.reduce((acc, r) => acc + score(r.them), 0) / c.rows.length) * 100);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: c.title,
      description: c.description,
      brand: { '@type': 'Brand', name: 'ZuriHub' },
      offers: {
        '@type': 'Offer',
        url: `${site.baseUrl}/compare/${slug}`,
        price: '25',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: c.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    },
  ];

  return (
    <>
      <SEO title={c.title} description={c.description} path={`/compare/${slug}`} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="text-center lg:text-left">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-3 py-1 text-sm font-semibold text-brand-300">
                {c.eyebrow}
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                {c.headline}
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-300 lg:mx-0">
                {c.subhead}
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
                >
                  Start free trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/60 px-7 py-3.5 text-base font-bold text-slate-200 transition hover:bg-slate-800"
                >
                  Book a demo
                </a>
              </div>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="rounded-3xl border border-slate-700 bg-slate-900/60 p-8 text-center backdrop-blur">
                <p className="text-5xl font-extrabold text-brand-400">{zurihubScore}%</p>
                <p className="mt-2 font-semibold text-white">ZuriHub fit</p>
                <p className="mt-1 text-sm text-slate-400">Based on the comparison</p>
              </div>
              <div className="rounded-3xl border border-slate-700 bg-slate-900/60 p-8 text-center backdrop-blur">
                <p className="text-5xl font-extrabold text-slate-400">{themScore}%</p>
                <p className="mt-2 font-semibold text-white">{c.left} fit</p>
                <p className="mt-1 text-sm text-slate-400">Based on the comparison</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24" aria-labelledby="intro-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <p className="mx-auto max-w-3xl text-center text-xl leading-relaxed text-slate-700">
            {c.intro}
          </p>
        </div>
      </section>

      {/* Pain */}
      <section className="bg-slate-50 py-24" aria-labelledby="pain-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <h2 id="pain-heading" className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                Why teams leave {c.left}
              </h2>
              <ul className="mt-8 space-y-4">
                {c.pain.map((p) => (
                  <li key={p} className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-sm">
                    <X className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                    <span className="text-slate-700">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900">The ZuriHub difference</h3>
              <div className="mt-6 space-y-4">
                {c.highlights.map((h) => (
                  <div key={h.title} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                    <div>
                      <p className="font-semibold text-slate-900">{h.title}</p>
                      <p className="text-sm text-slate-600">{h.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-24" aria-labelledby="table-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="table-heading" className="section-title">Feature comparison</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              See how the two platforms stack up on the features that matter most.
            </p>
          </div>
          <div className="mt-14 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-6 py-4 font-semibold">Feature</th>
                  <th className="px-6 py-4 font-semibold text-brand-700">{c.right}</th>
                  <th className="px-6 py-4 font-semibold">{c.left}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {c.rows.map((r) => (
                  <tr key={r.feature} className="hover:bg-slate-50/50">
                    <td className="px-6 py-4 font-semibold text-slate-900">{r.feature}</td>
                    <td className="px-6 py-4">{renderValue(r.zurihub)}</td>
                    <td className="px-6 py-4">{renderValue(r.them)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-slate-900 py-24 text-white" aria-labelledby="highlights-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="highlights-heading" className="section-title text-white">Why teams choose ZuriHub</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl text-slate-400">
              The reasons teams make the switch.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {c.highlights.map((h, i) => {
              const icons = [TrendingUp, DollarSign, Zap, Shield, Users];
              const HIcon = icons[i % icons.length];
              return (
                <div key={h.title} className="rounded-3xl border border-slate-700 bg-slate-800 p-8 transition hover:-translate-y-1 hover:bg-slate-700">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-600 text-white">
                    <HIcon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{h.title}</h3>
                  <p className="mt-2 text-slate-300">{h.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Migration */}
      <section className="py-24" aria-labelledby="migration-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="migration-heading" className="section-title">Switch in four steps</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Most teams are up and running in a day.
            </p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {c.migration.map((m) => (
              <div key={m.step} className="relative rounded-3xl border border-slate-100 bg-white p-8 pt-12 shadow-sm">
                <div className="absolute -top-5 left-8 flex h-10 w-10 items-center justify-center rounded-full bg-brand-600 text-base font-bold text-white shadow-lg">
                  {m.step}
                </div>
                <h3 className="text-lg font-bold text-slate-900">{m.title}</h3>
                <p className="mt-2 text-slate-600">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-100 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-4">
            {c.faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Ready to see the difference?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Start a free 14-day Pro trial and compare ZuriHub with your current tool firsthand.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Start free trial <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
